import { useEffect, useRef, useState } from 'react';

interface PullToRefreshOptions {
  onRefresh: () => Promise<void> | void;
  threshold?: number;
  enabled?: boolean;
}

interface PullToRefreshState {
  pulling: boolean;
  refreshing: boolean;
  pullDistance: number;
}

/**
 * Lightweight pull-to-refresh hook for a vertically scrolling element.
 * Triggers `onRefresh` when the user pulls down past `threshold`px while
 * the container is already scrolled to the top.
 */
export function usePullToRefresh<T extends HTMLElement>(
  ref: React.RefObject<T>,
  { onRefresh, threshold = 64, enabled = true }: PullToRefreshOptions,
): PullToRefreshState {
  const [state, setState] = useState<PullToRefreshState>({
    pulling: false,
    refreshing: false,
    pullDistance: 0,
  });
  const startYRef = useRef<number | null>(null);
  const triggeredRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (el.scrollTop > 0) {
        startYRef.current = null;
        return;
      }
      startYRef.current = e.touches[0]!.clientY!;
      triggeredRef.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (startYRef.current == null) return;
      const dy = e.touches[0]!.clientY! - startYRef.current;
      if (dy <= 0) {
        setState((s) => (s.pullDistance === 0 && !s.pulling ? s : { ...s, pulling: false, pullDistance: 0 }));
        return;
      }
      const damped = Math.min(dy * 0.5, threshold * 1.5);
      setState((s) => ({ ...s, pulling: true, pullDistance: damped }));
    };

    const handleTouchEnd = async () => {
      if (startYRef.current == null) return;
      const start = startYRef.current;
      startYRef.current = null;
      setState((s) => {
        if (s.pullDistance >= threshold && !triggeredRef.current) {
          triggeredRef.current = true;
          return { pulling: false, refreshing: true, pullDistance: 0 };
        }
        return { pulling: false, refreshing: s.refreshing, pullDistance: 0 };
      });
      void start; // suppress unused
      if (triggeredRef.current) {
        try {
          await onRefresh();
        } finally {
          setState((s) => ({ ...s, refreshing: false }));
          triggeredRef.current = false;
        }
      }
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    el.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [ref, onRefresh, threshold, enabled]);

  return state;
}
